import { Modules } from '@/configs/modules';
import { http, defaultConfig, getUniversityUrl } from './http';

export interface DoReservePayload {
  mealTypes: ReserveMealEntity[];
  weekDays: ReportDayEntity[];
}

export interface ReservePayload {
  foodTypeId: number;
  freeFoodSelected: boolean;
  mealTypeId: number;
  selectedCount: number;
}

export interface ChangeReserveSelfPayload {
  date: string;
  selfIds: string | number;
  groupId: string | number;
  mealTypeId: string | number;
  foodTypeId: string | number;
  foodId: string | number;
  besideFoodIds: string | number;
}

export interface SelfPorgramResponse {
  selfWeekPrograms: SelfMealEntity[][];
  mealTypes: ReserveMealEntity[];
  monthValidTotalCount: number;
  userWeekReserves?: UserWeekReserveEntity[];
}

export interface GetSelfProgramBody {
  showingDate: string;
  selectedSelfId: number;
}

export interface ChangeSelfPayload {
  date: string;
  reserveId: number;
  groupId: number;
  programId: number;
  mealTypeId: number;
  foodTypeId: number;
  foodId: number;
  sourceSelfId: number;
  destinationSelfId: number;
}

export const fetchCurrentReserves = async () => {
  const baseURL = getUniversityUrl();
  return await http.get<{ payload: DoReservePayload }>('/rest/reserves?weekStartDate=', defaultConfig(baseURL));
};

export const fetchCurrentDailySaleReserves = async () => {
  const baseURL = getUniversityUrl();
  return await http.get<HttpResponse<DailySaleProgramEntity>>('/rest/users/me/reserve-daily-sales', defaultConfig(baseURL));
};

export const fetchDailySalesProgram = async () => {
  const baseURL = getUniversityUrl();
  return await http.get('/rest/daily-sell-programs', defaultConfig(baseURL));
};

export const fetchSelfProgram = async ({ selectedSelfId, showingDate }: GetSelfProgramBody) => {
  const baseURL = getUniversityUrl();
  return http.get<{ payload: SelfPorgramResponse }>(
    `/rest/programs/v2?selfId=${selectedSelfId}&weekStartDate=${showingDate}`,
    defaultConfig(baseURL)
  );
};

export const listVoteReasons = async () => {
  const baseURL = getUniversityUrl();
  const url = '/rest/reserves/votes/reasons';
  const res = (await (http.get<HttpResponse<VoteRatingReasonEntity[]>>(url, defaultConfig(baseURL))));
  return res?.data;
};

export const listVotable = async () => {
  const baseURL = getUniversityUrl();
  const url = '/rest/reserves/votable';
  const res = (await (http.get<HttpResponse<ReserveVotableEntity[]>>(url, defaultConfig(baseURL))));
  return res?.data;
};

export const reserve = async (programId: number, data: ReservePayload) => {
  const baseURL = getUniversityUrl();
  const url = `/rest/reserves/${programId}/reserve`;
  const res = (await (http.put<HttpResponse<any>>(url, { ...data, selected: true }, defaultConfig(baseURL))));
  return res?.data;
};

export const reserveNewAPI = async (programId: number, data: { count: number; }) => {
  const baseURL = getUniversityUrl();
  const url = `/rest/reserves`;
  const res = (await (http.post<HttpResponse<any>>(url, {
    programId,
    app: 'MOBILE_APP',
    ...data
  }, defaultConfig(baseURL))));
  return res?.data;
};

export const cancel = async (programId: number, data: ReservePayload) => {
  const baseURL = getUniversityUrl();
  const url = `/rest/reserves/${programId}/reserve`;
  const res = (await (http.put<HttpResponse<any>>(url, { ...data, selected: false }, defaultConfig(baseURL))));
  return res?.data;
};

export const vote = async (reserveId: number, payload: CreateReserveVoteEntity) => {
  const baseURL = getUniversityUrl();
  const url = `/rest/reserves/${reserveId}/votes`;
  const res = (await (http.post<HttpResponse<any>>(url, payload, defaultConfig(baseURL))));
  return res?.data;
};

export const fetchReserveReport = async (weekStartDate = '', selfType: Modules | string) => {
  const baseURL = getUniversityUrl();
  const url = `/rest/reserves?weekStartDate=${weekStartDate}&selfType=${selfType}`;
  const res = (await (http.get<HttpResponse<GetReserveReportResponse>>(url, defaultConfig(baseURL))));
  return res?.data;
};

export const sellReserve = async (reserveId: number) => {
  const baseURL = getUniversityUrl();
  const url = `/rest/reserves/v2/${reserveId}/sell-reserve`;
  const res = await http.put(url, {}, defaultConfig(baseURL));
  return res?.data;
};

export const cancelSoldReserve = async (reserveId: number) => {
  const baseURL = getUniversityUrl();
  const url = `/rest/reserves/v2/${reserveId}/cancel-sold-reserve`;
  const res = await http.put(url, {}, defaultConfig(baseURL));
  return res?.data;
};

export async function buyFreeFood(reserveId: number) {
  const baseURL = getUniversityUrl();
  const url = `/rest/reserves/${reserveId}/reserve`;
  const res = await http.put(url, {
    selected: true,
    selectedCount: 1,
    freeFoodSelected: true,
  }, defaultConfig(baseURL));
  return res?.data;
}

export const changeSelf = async (data: ChangeSelfPayload) => {
  const baseURL = getUniversityUrl();
  return (await http.put('/rest/reserves/change-self', data, defaultConfig(baseURL))).data;
};
